import { useQuery } from '@tanstack/react-query';

export interface AccessIdentity {
  email?: string;
  name?: string;
}

// Browsers that have passed Cloudflare Access (i.e. visited /admin) get a hint
// flag in localStorage. We only probe the identity endpoint when that hint is
// present — so anonymous visitors never hit /cdn-cgi/access/get-identity and
// never produce its (benign but noisy) 400 in the console. The hint is just a
// UI optimisation; the real gate is Cloudflare Access, not this flag.
const OWNER_HINT_KEY = 'cook:owner-hint';

function hasOwnerHint(): boolean {
  try {
    return localStorage.getItem(OWNER_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function setOwnerHint(on: boolean): void {
  try {
    if (on) localStorage.setItem(OWNER_HINT_KEY, '1');
    else localStorage.removeItem(OWNER_HINT_KEY);
  } catch {
    // localStorage unavailable (private mode etc.) — ignore.
  }
}

/**
 * Call when the visitor is known to have passed Access (e.g. they're on an
 * /admin route, which is gated by Access at the edge). Enables the identity
 * probe on subsequent public pages so the owner sees admin affordances.
 */
export function noteAdminVisit(): void {
  setOwnerHint(true);
}

/**
 * Cloudflare Access exposes the logged-in user's identity at this endpoint.
 * It returns 200 + JSON when the visitor has passed Access, otherwise a
 * non-2xx response (or a login redirect). There is no code-level auth in this
 * app — Access is the only gate — so this is how the public UI learns whether
 * the current visitor is the authenticated owner.
 *
 * In local `vite dev` the endpoint doesn't exist, so this resolves to null and
 * admin-only UI stays hidden.
 */
async function fetchIdentity(): Promise<AccessIdentity | null> {
  try {
    const res = await fetch('/cdn-cgi/access/get-identity', {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      // No live Access session (e.g. it expired). Drop the hint so we stop
      // probing until the owner passes Access again.
      setOwnerHint(false);
      return null;
    }
    const data = (await res.json()) as AccessIdentity;
    if (data?.email) {
      setOwnerHint(true);
      return data;
    }
    setOwnerHint(false);
    return null;
  } catch {
    return null;
  }
}

function isLocalDev(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

export function useIsAuthenticated(): boolean {
  const { data } = useQuery({
    queryKey: ['access-identity'],
    queryFn: fetchIdentity,
    staleTime: 5 * 60 * 1000,
    retry: false,
    // Only probe in browsers that have been through Access. Anonymous visitors
    // skip the request entirely (no console 400).
    enabled: hasOwnerHint(),
  });
  // Local dev has no Cloudflare Access — treat localhost as the owner so the
  // admin + favourite affordances are always visible while developing.
  return isLocalDev() || !!data;
}
